import React, { Dispatch, SetStateAction, useState } from "react";
import { useAddProductMutation } from "@/redux/feature/product/productApi";
import toast from "react-hot-toast";
import { useAppSelector } from "@/redux/hooks/hooks";
import FileUploadFieldPlus from "@/components/ui/FileUploadFieldPlus";
import SingleSelector from "@/components/ui/SingleSelector";
import { TOption } from "@/types/types";
import CreditIcon from "@/assets/icons/CreditCoin.svg";

interface FormData {
  productName: string;
  websiteUrl: string;
  country: string;
}

interface AddProductModalProps {
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

const initialForm: FormData = {
  productName: "",
  websiteUrl: "",
  country: "",
};

// Country options data
const countryOptions: TOption[] = [
  {
    id: "IN",
    title: "India",
  },
  {
    id: "US",
    title: "United States",
  },
  {
    id: "GB",
    title: "United Kingdom",
  },
  {
    id: "CA",
    title: "Canada",
  },
  {
    id: "AU",
    title: "Australia",
  },
];

const AddProductModal: React.FC<AddProductModalProps> = ({ setIsOpen }) => {
  const [formData, setFormData] = useState<FormData>(initialForm);
  const [selectedCountry, setSelectedCountry] = useState<TOption | undefined>(
    undefined
  );
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [addProduct, { isLoading, isError }] = useAddProductMutation();

  console.log(isError);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleCountryChange = (selected: TOption | undefined) => {
    setSelectedCountry(selected);
    setFormData((prev) => ({
      ...prev,
      country: selected ? selected.id : "",
    }));

    // Clear country error if exists
    if (errors.country) {
      setErrors((prev) => ({ ...prev, country: undefined }));
    }
  };

  const validate = () => {
    const newErrors: Partial<FormData> = {};

    const urlRegex = /^(https?:\/\/)?([\w\d-]+\.)+[\w\d]{2,}(\/.*)?$/i;

    if (!formData.productName.trim()) newErrors.productName = "Required";
    if (!formData.websiteUrl.trim()) {
      newErrors.websiteUrl = "Required";
    } else if (!urlRegex.test(formData.websiteUrl.trim())) {
      newErrors.websiteUrl = "Please enter a valid URL";
    }
    if (!formData.country.trim()) newErrors.country = "Required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const email = useAppSelector((state) => state.auth.user?.email);

  const handleAdd = async () => {
    if (!validate()) return;

    const payload = {
      email: email,
      products: [
        {
          name: formData.productName,
          product_website_url: formData.websiteUrl,
          country: formData.country,
        },
      ],
    };
    console.log(payload);

    try {
      await addProduct(payload).unwrap();
      setIsOpen(false);
      toast.success("Product added!");
      setFormData(initialForm);
      setSelectedCountry(undefined);
    } catch (err: any) {
      console.error("Add product error:", err);

      const detail = err?.data?.detail;

      console.log(err);

      if (
        typeof detail === "string" &&
        detail.includes("is already present for this user")
      ) {
        toast.error("This product already exists");
      } else {
        toast.error("Failed to add product. Please try again.");
      }
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <div className="flex items-center gap-x-3">
          <h2 className="text-xl font-semibold text-black">Add Product</h2>
          {/* Credit Badge */}
          <div className="flex items-center gap-2 px-2 py-[2px] bg-amber-50 border border-amber-200 rounded-full">
            {/* <div className="w-2 h-2 bg-amber-400 rounded-full"></div> */}
            <CreditIcon />
            <span className="text-amber-700 text-[10px] font-medium">
              1 Credit Required
            </span>
          </div>
        </div>
        <p className="text-gray-600 text-sm mt-1">
          Fill in the product details
        </p>
      </div>

      {/* <div>
        <label className="block text-sm font-medium text-black mb-2">
          Product Image
        </label>
        <FileUploadFieldPlus
          name="imgUrl"
          identifier={email as string}
          accept={{
            "image/png": [],
            "image/jpeg": [],
            "image/jpg": [],
            "image/webp": [],
          }}
          maxFiles={1}
          onFilesUploaded={(files) => {
            if (files.length > 0 && files[0].live_url) {
              setFormData({
                ...formData,
                imgUrl: files[0].live_url as string,
              });

              if (errors.imgUrl) {
                setErrors((prev) => ({ ...prev, imgUrl: undefined }));
              }
            }
          }}
        />
        {errors.imgUrl && (
          <p className="text-red-500 text-xs mt-1">{errors.imgUrl}</p>
        )}
      </div> */}

      <div className="w-full gap-4">
        <div>
          <label className="block text-sm font-medium text-black mb-2">
            Product Name *
          </label>
          <input
            type="text"
            name={"productName"}
            value={formData.productName}
            onChange={handleChange}
            className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-1 ${
              errors.productName
                ? "border-red-500 ring-red-500"
                : "border-gray-300 focus:border-black focus:ring-black"
            }`}
            placeholder={"Polo Shirts"}
          />
          {errors.productName && (
            <p className="text-red-500 text-xs mt-1">
              {errors.productName as string}
            </p>
          )}
        </div>
      </div>

      <div className="w-full">
        <label className="block text-sm font-medium text-black mb-2">
          Country *
        </label>
        <div className="w-full">
          <SingleSelector
            name="country"
            data={selectedCountry}
            options={countryOptions}
            onChange={handleCountryChange}
          />
        </div>
        {errors.country && (
          <p className="text-red-500 text-xs mt-1">{errors.country}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-black mb-2">
          Product Webpage *
        </label>
        <input
          type="text"
          name={"websiteUrl"}
          value={formData.websiteUrl}
          onChange={handleChange}
          className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-1 ${
            errors["websiteUrl" as keyof FormData]
              ? "border-red-500 ring-red-500"
              : "border-gray-300 focus:border-black focus:ring-black"
          }`}
          placeholder={"Place your website url"}
        />
        {errors.websiteUrl && (
          <p className="text-red-500 text-xs mt-1">{errors.websiteUrl}</p>
        )}
      </div>

      <div className="flex gap-3 pt-6 border-t">
        <button
          onClick={() => setIsOpen(false)}
          disabled={isLoading}
          className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handleAdd}
          disabled={isLoading}
          className="flex-1 px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 flex items-center justify-center"
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
              Adding...
            </>
          ) : (
            "Add Product"
          )}
        </button>
      </div>
    </div>
  );
};

export default AddProductModal;
